import { useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MARKER = 'data-injected-user-script';

/**
 * Injects admin-configured tracker/analytics scripts into <head> and end of <body>.
 * Iterates the `custom_scripts` list (each item has: name, placement, code, enabled).
 * Falls back to legacy custom_head_scripts / custom_body_scripts strings if list is empty.
 * Runs once on app mount. Cleans up injected nodes on unmount.
 */
const injectHtml = (html, targetEl, meta) => {
  if (!html || !String(html).trim()) return;
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  Array.from(wrapper.childNodes).forEach((node) => {
    if (node.nodeType === 1) {
      if (node.tagName === 'SCRIPT') {
        const script = document.createElement('script');
        Array.from(node.attributes).forEach((a) => script.setAttribute(a.name, a.value));
        script.text = node.textContent;
        script.setAttribute(MARKER, meta);
        targetEl.appendChild(script);
      } else {
        node.setAttribute(MARKER, meta);
        targetEl.appendChild(node);
      }
    }
  });
};

const removeInjected = () => {
  document.querySelectorAll(`[${MARKER}]`).forEach((el) => el.remove());
};

export const CustomScriptsInjector = () => {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/site/content`, { params: { lang: 'az' } });
        if (cancelled) return;
        removeInjected();
        const list = Array.isArray(data.custom_scripts) ? data.custom_scripts : [];
        if (list.length > 0) {
          list.filter((s) => s && s.enabled && s.code && s.code.trim()).forEach((s) => {
            const target = s.placement === 'body' ? document.body : document.head;
            injectHtml(s.code, target, s.id || s.name || 'anon');
          });
        } else {
          // Legacy fallback for pre-Phase-39 saved content
          injectHtml(data.custom_head_scripts, document.head, 'legacy-head');
          injectHtml(data.custom_body_scripts, document.body, 'legacy-body');
        }
      } catch (err) {
        // silent — trackers must never break the app
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return null;
};

export default CustomScriptsInjector;
